import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import PagyPagination from '@/components/PagyPagination'
import { useOrgPath } from '@/lib/pathFor'
import { formatShortDateTime, formatDurationFromSeconds } from '@/lib/formatters'
import { capitalize } from '@/lib/utils'
import { AGENT_SESSION_STATUS_COLORS } from '@/lib/constants'

interface SessionItem {
  id: string
  id_short: string
  thread_subject: string | null
  shipment_label: string | null
  status: string
  escalation_reason: string | null
  tool_call_count: number
  total_tokens: number
  duration_seconds: number | null
  created_at: string
  show_url: string
}

interface AgentSessionsIndexProps {
  sessions: SessionItem[]
  filter: string
  search: string
  pagy: { page: number; pages: number; count: number; next: number | null; prev: number | null }
}

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'needs_attention', label: 'Needs Attention' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
  { key: 'escalated', label: 'Escalated' },
] as const

export default function AgentSessionsIndex({ sessions, filter, search: initialSearch, pagy }: AgentSessionsIndexProps) {
  const [search, setSearch] = useState(initialSearch)
  const pathFor = useOrgPath()

  function switchFilter(f: string) {
    router.get(pathFor('/agent_sessions'), { filter: f, search }, { preserveState: true })
  }

  function handleRetry(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    router.post(pathFor(`/agent_sessions/${id}/retry`), {}, { preserveScroll: true })
  }

  return (
    <>
      <header className="flex h-14 items-center border-b px-8">
        <h1 className="text-sm font-semibold mr-6">Agent Sessions</h1>
        <div className="flex items-end h-full">
          {FILTERS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => switchFilter(key)}
              className={`cursor-pointer flex items-center h-8 px-3 text-[13px] border-b-2 ${
                filter === key
                  ? 'font-semibold text-primary border-primary'
                  : 'font-medium text-muted-foreground border-transparent hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <form onSubmit={(e) => { e.preventDefault(); router.get(pathFor('/agent_sessions'), { search, filter }, { preserveState: true }) }} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by thread subject..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-[200px] pl-8 text-[13px]"
              />
            </div>
          </form>
        </div>
      </header>

      <p className="border-b px-8 py-2 text-xs text-muted-foreground">
        The agent processes inbound email automatically. Runs that failed or were escalated also appear on the{' '}
        <Link href={pathFor('/')} className="underline hover:text-foreground">Dashboard</Link> under Needs attention.
      </p>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">ID</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Thread</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Tool Calls</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Tokens</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Duration</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Date</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground"></th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <tr
                key={session.id}
                className="border-b hover:bg-muted/30 cursor-pointer"
                onClick={() => router.visit(session.show_url)}
              >
                <td className="px-6 py-3">
                  <Link href={session.show_url} className="text-[13px] font-mono text-foreground hover:underline">
                    {session.id_short}
                  </Link>
                </td>
                <td className="px-6 py-3">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[13px] text-foreground font-medium truncate max-w-[200px]">
                      {session.thread_subject || '(no subject)'}
                    </span>
                    {session.shipment_label && (
                      <span className="text-[11px] text-muted-foreground">{session.shipment_label}</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-3">
                  <div className="flex flex-col gap-1">
                    <Badge variant="secondary" size="sm" className={`font-medium ${AGENT_SESSION_STATUS_COLORS[session.status] || 'bg-gray-100 text-gray-600'}`}>
                      {capitalize(session.status)}
                    </Badge>
                    {session.escalation_reason && (
                      <span className="text-[10px] text-amber-700 truncate max-w-[150px]" title={session.escalation_reason}>
                        {session.escalation_reason}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-3 text-[13px] text-foreground">{session.tool_call_count}</td>
                <td className="px-6 py-3 text-[13px] text-muted-foreground">{session.total_tokens.toLocaleString()}</td>
                <td className="px-6 py-3 text-[13px] text-muted-foreground">{formatDurationFromSeconds(session.duration_seconds)}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{formatShortDateTime(session.created_at)}</td>
                <td className="px-6 py-3">
                  {(session.status === 'failed' || session.status === 'escalated') && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={(e) => handleRetry(session.id, e)}>
                      Retry
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {sessions.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-8 text-center text-sm text-muted-foreground">No sessions found</td></tr>
            )}
          </tbody>
        </table>
        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

AgentSessionsIndex.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>{page}</AuthenticatedLayout>
)
